import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Check } from 'lucide-react';
import clsx from 'clsx';
import { useWizardStore } from '../../stores/wizardStore';
import { fetchPlaybooks } from '../../lib/api';
import type { PlaybookSummary } from '../../types/wizard';

interface SectionProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function ReviewSection({ title, defaultOpen = false, children }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border border-dark-border rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 bg-surface-DEFAULT hover:bg-dark-hover transition-colors"
      >
        <span className="text-sm font-semibold text-white">{title}</span>
        {open ? (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-500" />
        )}
      </button>
      {open && (
        <div className="px-5 py-4 border-t border-dark-border bg-dark-card space-y-2">
          {children}
        </div>
      )}
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string | string[] | boolean | number }) {
  let displayValue: React.ReactNode;

  if (typeof value === 'boolean') {
    displayValue = value ? (
      <span className="flex items-center gap-1 text-green-400">
        <Check className="h-3.5 w-3.5" /> Enabled
      </span>
    ) : (
      <span className="text-gray-500">Disabled</span>
    );
  } else if (Array.isArray(value)) {
    if (value.length === 0) {
      displayValue = <span className="text-gray-600 italic">None selected</span>;
    } else {
      displayValue = (
        <div className="flex flex-wrap gap-1.5">
          {value.map((v) => (
            <span
              key={v}
              className="px-2 py-0.5 rounded bg-surface-100 border border-dark-border text-xs text-gray-300"
            >
              {v}
            </span>
          ))}
        </div>
      );
    }
  } else if (typeof value === 'number') {
    displayValue = <span className="text-gray-300 font-mono">{value}</span>;
  } else {
    displayValue = value ? (
      <span className="text-gray-300">{value}</span>
    ) : (
      <span className="text-gray-600 italic">Not set</span>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2">
      <span className="text-xs font-medium text-gray-500 sm:w-40 shrink-0 uppercase tracking-wider">
        {label}
      </span>
      <div className="text-sm">{displayValue}</div>
    </div>
  );
}

export default function ReviewStep() {
  const {
    projectContext,
    techStack,
    teamStructure,
    gitWorkflow,
    codingStandards,
    mcpConfig,
    cicd,
    plugins,
    selectedPlaybook,
  } = useWizardStore();

  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [playbookInfo, setPlaybookInfo] = useState<PlaybookSummary | null>(null);

  useEffect(() => {
    if (!selectedPlaybook) {
      setPlaybookInfo(null);
      return;
    }
    let cancelled = false;
    fetchPlaybooks().then((playbooks) => {
      if (cancelled) return;
      setPlaybookInfo(playbooks.find((p) => p.id === selectedPlaybook) ?? null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [selectedPlaybook]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">
          Review Configuration
        </h2>
        <p className="text-sm text-gray-500">
          Review your selections before generating your blueprint.
        </p>
      </div>

      <div className="space-y-3">
        <ReviewSection title="Project Context" defaultOpen>
          <ReviewRow label="Name" value={projectContext.name} />
          <ReviewRow label="Goal" value={projectContext.goal} />
          <ReviewRow label="Description" value={projectContext.description} />
          <ReviewRow label="Industry" value={projectContext.industry} />
          <ReviewRow label="Compliance" value={projectContext.compliance} />
        </ReviewSection>

        <ReviewSection title="Tech Stack">
          <ReviewRow label="Languages" value={techStack.languages} />
          <ReviewRow label="Frameworks" value={techStack.frameworks} />
          <ReviewRow label="Databases" value={techStack.databases} />
          <ReviewRow label="Cloud" value={techStack.cloudProvider} />
          <ReviewRow label="Infrastructure" value={techStack.infrastructure} />
          <ReviewRow label="Package Manager" value={techStack.packageManager} />
          <ReviewRow label="Monorepo" value={techStack.monorepo} />
          {techStack.monorepo && (
            <ReviewRow label="Monorepo Tool" value={techStack.monorepoTool} />
          )}
        </ReviewSection>

        <ReviewSection title="Team Structure">
          <ReviewRow label="Size" value={teamStructure.size} />
          <ReviewRow
            label="Seniority"
            value={`Junior ${teamStructure.seniorityMix.junior}% / Mid ${teamStructure.seniorityMix.mid}% / Senior ${teamStructure.seniorityMix.senior}% / Lead ${teamStructure.seniorityMix.lead}%`}
          />
          <ReviewRow label="Claude Users" value={teamStructure.claudeUsers} />
          <ReviewRow label="Reviewers" value={teamStructure.dedicatedReviewers} />
          <ReviewRow label="Timezones" value={teamStructure.timezones} />
        </ReviewSection>

        <ReviewSection title="Git Workflow">
          <ReviewRow label="Branching" value={gitWorkflow.branchingStrategy} />
          <ReviewRow label="Branch Naming" value={gitWorkflow.branchNaming} />
          <ReviewRow label="Commits" value={gitWorkflow.commitStandard} />
          <ReviewRow label="Signed" value={gitWorkflow.signedCommits} />
          <ReviewRow label="PR Requirements" value={gitWorkflow.prRequirements} />
          <ReviewRow label="Protected" value={gitWorkflow.protectedBranches} />
        </ReviewSection>

        <ReviewSection title="Coding Standards">
          <ReviewRow label="Linting" value={codingStandards.linting} />
          <ReviewRow label="Formatting" value={codingStandards.formatting} />
          <ReviewRow label="Tests" value={codingStandards.testRequirements} />
          <ReviewRow label="Coverage" value={`${codingStandards.coverageThreshold}%`} />
          <ReviewRow label="Architecture" value={codingStandards.architecture} />
          <ReviewRow label="Sensitive Paths" value={codingStandards.sensitivePaths} />
          <ReviewRow label="Dependencies" value={codingStandards.dependencyPolicy} />
          <ReviewRow label="Max File Length" value={codingStandards.maxFileLength} />
          <ReviewRow label="Naming" value={codingStandards.namingConventions} />
          <ReviewRow label="Custom Conventions" value={codingStandards.customConventions} />
        </ReviewSection>

        <ReviewSection title="MCP Configuration">
          <ReviewRow label="Tickets" value={mcpConfig.ticketSystem} />
          <ReviewRow label="Docs" value={mcpConfig.documentation} />
          <ReviewRow label="Communication" value={mcpConfig.communication} />
          <ReviewRow label="Source Control" value={mcpConfig.sourceControl} />
          <ReviewRow label="Cloud Console" value={mcpConfig.cloudConsole} />
          <ReviewRow label="Monitoring" value={mcpConfig.monitoring} />
          <ReviewRow
            label="Custom Servers"
            value={
              mcpConfig.customServers.length > 0
                ? mcpConfig.customServers.map((s) => s.name || 'Unnamed').join(', ')
                : ''
            }
          />
        </ReviewSection>

        <ReviewSection title="CI/CD">
          <ReviewRow label="Platform" value={cicd.platform} />
          <ReviewRow label="Stages" value={cicd.stages} />
          <ReviewRow label="Auto-Deploy" value={cicd.autoDeploy} />
          <ReviewRow label="AI Code Flag" value={cicd.aiCodeFlag} />
          <ReviewRow label="Code Ownership" value={cicd.codeOwnership} />
        </ReviewSection>

        <ReviewSection title="Plugins">
          <ReviewRow label="Selected" value={plugins} />
        </ReviewSection>

        <ReviewSection title="Playbook" defaultOpen>
          {playbookInfo ? (
            <div className="space-y-3">
              <ReviewRow label="Playbook" value={playbookInfo.name} />
              <ReviewRow label="Phases" value={String(playbookInfo.phaseCount)} />
              <div className="px-1">
                <p className="text-xs text-gray-500 mb-2">Phase Roadmap</p>
                <div className="space-y-1.5">
                  {playbookInfo.phases.map((phase, i) => (
                    <div key={phase.id} className="flex items-center gap-2 text-sm">
                      <span className="w-6 h-6 rounded-full bg-accent/10 border border-accent/20 text-accent-300 flex items-center justify-center text-xs font-semibold shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-gray-300">{phase.name}</span>
                      <span className="text-gray-600 text-xs ml-auto">
                        {phase.promptCount} prompt{phase.promptCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 px-1">
              No playbook selected — project will be generated without phase-based methodology.
            </p>
          )}
        </ReviewSection>
      </div>

      {/* Save as Template */}
      <div className="border border-dark-border rounded-lg p-5 bg-surface-DEFAULT">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-white">
              Save as Template
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Share your configuration with the community
            </p>
          </div>
          <button
            onClick={() => setSaveAsTemplate(!saveAsTemplate)}
            className={clsx(
              'relative w-11 h-6 rounded-full transition-colors duration-200',
              saveAsTemplate ? 'bg-accent' : 'bg-surface-300'
            )}
          >
            <div
              className={clsx(
                'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200',
                saveAsTemplate && 'translate-x-5'
              )}
            />
          </button>
        </div>

        {saveAsTemplate && (
          <div className="space-y-3 pt-2 border-t border-dark-border">
            <div className="mt-3">
              <label className="block text-xs text-gray-500 mb-1">
                Template Name
              </label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Next.js Enterprise Starter"
                className="input-field py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Description
              </label>
              <textarea
                value={templateDesc}
                onChange={(e) => setTemplateDesc(e.target.value)}
                placeholder="Describe this template..."
                rows={3}
                className="input-field py-2 text-sm resize-none"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
